import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
} from "@mui/material";
import {defineMessage, FormattedMessage} from "react-intl";
import Loading from "@/components/breadcrumbs/Loading";
import {Page} from "@/components/pages";
import {useHookClass} from "@dvrt/graphql-widgets";
import DataSource from "@/components/gateway/routing/DataSource";
import {useBoolState, useDomainNameQuery} from "@/hooks";
import RoutingForm from "@/components/gateway/routing/RoutingForm";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    DestinationsQueryHookResult,
    GatewaysQueryHookResult,
    useDestinationsQuery,
    useGatewaysQuery,
} from "@/components/gateway/routing/queries.generated";
import {
    NumberField,
    FormActionButtons,
    FormattedFormRow,
    FormContainer,
    FormRowDivider,
    FormattedAlert,
} from "@dvrt/graphql-widgets";
import FormattedButton from "@/components/FormattedButton";
import {useStyles} from "@/components/call-group/members/styles";
import NoRequiredData from "@/components/gateway/routing/NoRequiredData";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import MultiTooltipWrapper from "@/components/MultiTooltipWrapper";

type Props = {
    domainId: number;
};

type ContentProps = {
    dataSource: DataSource;
    gatewaysQuery: GatewaysQueryHookResult;
    destinationsQuery: DestinationsQueryHookResult;
    domainName: string;
};

const Content = ({dataSource, gatewaysQuery, destinationsQuery, domainName}: ContentProps) => {
    const form = new RoutingForm(
        dataSource,
        destinationsQuery.data.destination.items.map((destination) => destination.id),
        gatewaysQuery.data.gateways.items.map((gateway) => gateway.id),
    );
    const [enabled, enable, disable] = useBoolState(true);
    const {classes} = useStyles();
    const auth = useAuth();

    const routingsCount = Object.values(form.routings).flat().length;
    const canEditOperator = auth.hasPermission("edit_superuser_gateway", ContextFilters.domain(domainName));

    // do not show the 'add' part of the message if the user has no permission to add
    const noRulesTranslation = canEditOperator
        ? defineMessage({id: "gatewayRouting.noRules", defaultMessage: 'There are no rules! Hit the "Add" to add.'})
        : defineMessage({id: "gatewayRouting.noRulesNoPermission", defaultMessage: "There are no rules!"});

    async function submit() {
        disable();
        const response = await form.submit();
        if (response) {
            await dataSource.fetch();
        }
        enable();
    }

    return (
        <Page title={defineMessage({id: "gatewayRouting.pageTitle", defaultMessage: "Routing"})}>
            <FormContainer>
                {routingsCount > 0 ? (
                    <Table size={"small"} style={{marginBottom: "75px"}}>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <FormattedMessage id="gatewayRouting.gateway" defaultMessage="Gateway" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="gatewayRouting.destination" defaultMessage="Destination" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="gatewayRouting.priority" defaultMessage="Priority" />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage
                                        id="gatewayRouting.lastCallDirection"
                                        defaultMessage="Last call direction"
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage
                                        id="gatewayRouting.lastCallTime"
                                        defaultMessage="Last call time"
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="gatewayRouting.comment" defaultMessage="Comment" />
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        arrow={true}
                                        placement={"top"}
                                        title={
                                            form.canAddNewRouting() ? (
                                                <FormattedMessage
                                                    id="gatewayRouting.buttons.tooltips.add"
                                                    defaultMessage="Add new rule"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id="gatewayRouting.noMoreAvailableCombinations"
                                                    defaultMessage="All routing combinations have been exhausted."
                                                />
                                            )
                                        }
                                    >
                                        <span>
                                            <IconButton
                                                color="primary"
                                                size="small"
                                                onClick={() => form.addNextRouting()}
                                                disabled={!enabled || !canEditOperator || !form.canAddNewRouting()}
                                            >
                                                <FontAwesomeIcon icon="plus-square" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {form.routingsAsArray.map((routing, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <MultiTooltipWrapper
                                            tooltipTitles={[
                                                {
                                                    condition: !form.canAddNewRouting(),
                                                    translation: {
                                                        translationId: defineMessage({
                                                            id: "gatewayRouting.noMoreAvailableCombinations",
                                                            defaultMessage:
                                                                "All routing combinations have been exhausted.",
                                                        }),
                                                    },
                                                },
                                                {
                                                    condition: !form.getAvailableGatewaysForDestination(
                                                        routing.destinationId,
                                                    ).length,
                                                    translation: {
                                                        translationId: defineMessage({
                                                            id: "gatewayRouting.noAvailableGatewaysWithDestination",
                                                            defaultMessage:
                                                                "The destination has been set to all gateways.",
                                                        }),
                                                    },
                                                },
                                            ]}
                                            showFirstOnly={true}
                                        >
                                            <Select
                                                variant="standard"
                                                disabled={
                                                    !enabled ||
                                                    !canEditOperator ||
                                                    !form.canAddNewRouting() ||
                                                    !form.gatewaysWithEmptySpace.filter((gw) => gw != routing.gatewayId)
                                                        .length ||
                                                    !form.getAvailableGatewaysForDestination(routing.destinationId)
                                                        .length
                                                }
                                                value={routing.gatewayId}
                                                onChange={(ev) =>
                                                    form.updateRouting(routing, {
                                                        gatewayId: parseInt(ev.target.value.toString()),
                                                    })
                                                }
                                            >
                                                <MenuItem key={routing.gatewayId} value={routing.gatewayId}>
                                                    {
                                                        gatewaysQuery.data.gateways.items.find(
                                                            (gw) => gw.id == routing.gatewayId,
                                                        )?.displayName
                                                    }
                                                </MenuItem>

                                                {gatewaysQuery.data.gateways.items
                                                    .filter(
                                                        (gw) =>
                                                            gw.id !== routing.gatewayId &&
                                                            form.gatewaysWithEmptySpace.includes(gw.id),
                                                    )
                                                    .map((gw) => (
                                                        <MenuItem key={gw.id} value={gw.id}>
                                                            {gw.displayName}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </MultiTooltipWrapper>
                                    </TableCell>
                                    <TableCell>
                                        <MultiTooltipWrapper
                                            tooltipTitles={[
                                                {
                                                    condition: () => !form.canAddNewRouting(),
                                                    translation: {
                                                        translationId: defineMessage({
                                                            id: "gatewayRouting.noMoreAvailableCombinations",
                                                            defaultMessage:
                                                                "All routing combinations have been exhausted.",
                                                        }),
                                                    },
                                                },
                                                {
                                                    condition:
                                                        form.getAvailableDestinationsForGateway(routing.gatewayId)
                                                            .length === 0,
                                                    translation: {
                                                        translationId: defineMessage({
                                                            id: "gatewayRouting.noMoreDestinations",
                                                            defaultMessage:
                                                                "No other destination available for this gateway.",
                                                        }),
                                                    },
                                                },
                                            ]}
                                            showFirstOnly={true}
                                        >
                                            <Select
                                                variant="standard"
                                                disabled={
                                                    !enabled ||
                                                    !canEditOperator ||
                                                    !form.canAddNewRouting(routing.gatewayId)
                                                }
                                                value={routing.destinationId}
                                                onChange={(ev) =>
                                                    form.updateRouting(routing, {
                                                        destinationId: parseInt(ev.target.value.toString()),
                                                    })
                                                }
                                            >
                                                <MenuItem key={routing.destinationId} value={routing.destinationId}>
                                                    {
                                                        destinationsQuery.data.destination.items.find(
                                                            (dest) => dest.id == routing.destinationId,
                                                        )?.name
                                                    }
                                                </MenuItem>

                                                {destinationsQuery.data.destination.items
                                                    .filter((dest) =>
                                                        form
                                                            .getAvailableDestinationsForGateway(routing.gatewayId)
                                                            .includes(dest.id),
                                                    )
                                                    .map((dest) => (
                                                        <MenuItem key={dest.id} value={dest.id}>
                                                            {dest.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </MultiTooltipWrapper>
                                    </TableCell>
                                    <TableCell>
                                        <NumberField
                                            disabled={!enabled || !canEditOperator}
                                            variant="standard"
                                            value={routing.priority}
                                            update={(ev) => form.updateRouting(routing, {priority: ev})}
                                            min={1}
                                            fullWidth={false}
                                        />
                                    </TableCell>
                                    <TableCell style={{minWidth: "120px"}}>{routing.lastCallDirection}</TableCell>
                                    <TableCell style={{minWidth: "120px"}}>{routing.lastCallTime}</TableCell>
                                    <TableCell style={{minWidth: "150px"}}>
                                        <TextField
                                            variant="standard"
                                            disabled={!enabled || !canEditOperator}
                                            value={routing.comment}
                                            onChange={(ev) =>
                                                form.updateRouting(routing, {comment: ev.target.value.toString()})
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip
                                            arrow={true}
                                            placement={"top"}
                                            title={
                                                <FormattedMessage
                                                    id="gatewayRouting.buttons.tooltips.delete"
                                                    defaultMessage="Delete rule"
                                                />
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    disabled={!enabled || !canEditOperator}
                                                    color="secondary"
                                                    size="small"
                                                    onClick={() =>
                                                        form.removeRouting(routing.gatewayId, routing.destinationId)
                                                    }
                                                >
                                                    <FontAwesomeIcon icon="trash" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className={classes.noMembersContainer}>
                        <FormattedAlert message={noRulesTranslation} severity="info" />
                        {canEditOperator && (
                            <FormattedButton
                                message={defineMessage({
                                    id: "gatewayRouting.buttons.tooltips.add",
                                    defaultMessage: "Add new rule",
                                })}
                                onClick={() => form.addNextRouting()}
                                disabled={!enabled || !canEditOperator || !form.canAddNewRouting()}
                                style={{marginLeft: "1em"}}
                            />
                        )}
                    </div>
                )}
                {canEditOperator && (
                    <>
                        <FormRowDivider />
                        <FormattedFormRow>
                            <div style={{marginTop: "25px"}}>
                                <FormActionButtons
                                    mainAction={submit}
                                    secondaryAction={() => {
                                        form.reset();
                                    }}
                                    enabled={form.hasChanged && enabled}
                                />
                            </div>
                        </FormattedFormRow>
                    </>
                )}
            </FormContainer>
        </Page>
    );
};

export default ({domainId}: Props) => {
    const dataSource = useHookClass(DataSource, domainId);

    const domainNameQuery = useDomainNameQuery(domainId);
    const gatewaysQuery = useGatewaysQuery({variables: {domainId: domainId}, fetchPolicy: "no-cache"});
    const destinationsQuery = useDestinationsQuery({fetchPolicy: "no-cache"});

    if (!dataSource.loaded || gatewaysQuery.loading || destinationsQuery.loading) return <Loading />;
    if (!destinationsQuery.data.destination.items.length)
        return (
            <NoRequiredData
                message={defineMessage({
                    id: "gatewayRouting.noDestinations",
                    defaultMessage: "There are no custom routing destinations! Please add one before create new rule.",
                })}
                messageNoPermission={defineMessage({
                    id: "gatewayRouting.noDestinationsNoPermission",
                    defaultMessage: "There are no custom routing destinations!",
                })}
                link="/custom-routing-destinations"
                permission="edit_superuser_gateway"
                domainId={domainId}
            />
        );
    if (!gatewaysQuery.data.gateways.items.length)
        return (
            <NoRequiredData
                message={defineMessage({
                    id: "gatewayRouting.noGateways",
                    defaultMessage: "There are no gateways! Please add one before create new rule.",
                })}
                messageNoPermission={defineMessage({
                    id: "gatewayRouting.noGatewaysNoPermission",
                    defaultMessage: "There are no gateways!",
                })}
                link={`/domains/${domainId}/gateways/gateways`}
                permission="add_gateway"
                domainId={domainId}
            />
        );

    return (
        <Content
            dataSource={dataSource}
            gatewaysQuery={gatewaysQuery}
            destinationsQuery={destinationsQuery}
            domainName={domainNameQuery.domainName}
        />
    );
};
